/**
 * What a platform's app can be asked to do, read off the measured verb table.
 *
 * One question is asked in two places and has to be answered the same way in
 * both. The accounts page decides whether to offer an inbox sweep for an
 * account, and the inbox decides what to say about a thread with nothing in it
 * yet. If those two disagree, the product offers a button on a platform whose
 * transcript can never arrive, or explains an empty thread by pointing at a
 * button that is not there.
 *
 * So the answer is derived rather than listed. `METERED_ACTIONS_BY_CHANNEL` is
 * a mirror of the kernel's own table and is pinned against it by a build gate,
 * which means a platform gains a readable inbox here on the day the engine
 * measures one, with no edit in this file.
 */
import { METERED_ACTIONS_BY_CHANNEL } from './labels'
import type { ActionType, Channel } from './types'

/**
 * The measured verbs that read a conversation list.
 *
 * `connections_check` is the third housekeeping sweep and is deliberately not
 * one of them. It reads pending invitations, which is a different surface
 * answering a different question, so a platform carrying only that verb has no
 * inbox for anything here to fetch.
 */
/*
 * THE VERB THE ROUTE GATES ON, and only that one.
 *
 * This was ['list_conversations', 'inbox_check'] matched with `.some()` until
 * 2026-09-13, while POST /channel-accounts/:id/inbox-sweep refuses on
 * channelPerforms(channel, 'inbox_check') alone. The two agree for every channel
 * today, because every channel with a conversation list also has the check --
 * which is exactly why the difference was invisible. The day one has a list and
 * no check, this page draws a button the route answers with 409, and the
 * operator presses a control that cannot work.
 *
 * One verb, the same one, and the test beside this file compares the answer
 * against the route's own predicate for every channel rather than trusting this
 * comment.
 */
export const INBOX_READ_VERBS: readonly ActionType[] = ['inbox_check']

/**
 * The verb table widened to every channel.
 *
 * The table is keyed by the channels this build can run, and a `channel`
 * arrives off the wire, so the lookup has to be allowed to miss. Widening the
 * type rather than casting the value is what keeps that honest: a platform with
 * no row reads as a platform with no verbs, which is the true answer for it.
 */
const VERBS_BY_CHANNEL: Partial<Record<Channel, readonly ActionType[]>> = METERED_ACTIONS_BY_CHANNEL

/**
 * Whether a phone can be asked to sweep this platform's inbox.
 *
 * Must answer what the route answers. A button drawn where the route refuses is
 * a control that can only ever fail, which reads as a broken product rather than
 * as a platform with no way in.
 */
export function channelReadsItsInbox(channel: Channel): boolean {
  const verbs = VERBS_BY_CHANNEL[channel] ?? []
  return INBOX_READ_VERBS.every((verb) => verbs.includes(verb))
}

/**
 * Whether an account on this platform can be kept for reading profiles.
 *
 * Read off the same verb table, and pinned against the kernel's
 * `channelReadsProfiles` by the test beside this file, because the purpose
 * control offers "Reading profiles only" from this answer and the route that
 * saves a purpose refuses it from the kernel's.
 */
export function channelReadsProfiles(channel: Channel): boolean {
  return (VERBS_BY_CHANNEL[channel] ?? []).includes('profile_scan')
}
